/**
 * The farm rounds.
 *
 * These do not work like the main draft and never have. Since 2021 a GM does not select a
 * college player — he NOMINATES one, in serpentine order, and then everybody bids FAAB on him.
 * So a turn has two halves: the nomination, which belongs to whoever is up, and the sale, which
 * belongs to the room. The log has to record both, including the turns where somebody passes,
 * because "whose turn was it" is the question the farm log gets asked years later.
 *
 * Two rounds is the 2025 experiment (conditional — it lapses without a re-vote at the 2026
 * committee), so the round count is a knob rather than a constant.
 *
 * The prospect list is a convenience, not a constraint. Half the interesting farm picks in the
 * league's history were players no big board had heard of, so a typed-in name is always allowed.
 *
 * The list shows QB, RB, WR and TE only. The bank carries every position because the index page
 * wants them, but nobody has ever nominated a cornerback and offering 200 of them makes finding
 * the running back you meant harder. High schoolers ARE eligible and are simply in the list.
 */
const FANTASY_POSITIONS = ['QB', 'RB', 'WR', 'TE'];

import {
  useCallback, useEffect, useMemo, useRef, useState, type CSSProperties,
} from 'react';
import { faab, openingBalance } from '../domain/faab.js';
import {
  OPENING_BID, SOLD_DWELL_MS, auctionBar, auctionView, clearBids, clearNomination, farmBlock,
  closeUnsold, guardNominate, guardRaise, guardSell, justSold, nomCode, nomSoldCount, roundWinners,
  stillBidding, undoSale,
  unstarted, withOpeningBid, withPass, withoutPass, type Turn,
} from '../domain/farmAuction.js';
import { farmSpendEntry, farmSpendId } from '../domain/farmSpend.js';
import { NomGrid } from './NomGrid.js';
import { BidTable, type BidRow } from './BidTable.js';
import { PauseMark, PlayMark, RestartIcon, UndoMark } from './ClockMarks.js';
import { FarmBar } from './FarmBar.js';
import { bidHasSold, countdown } from '../domain/clocks.js';
import { heldSince } from '../domain/auctionTime.js';
import { AuctionCard } from './AuctionCard.js';
import { FarmPast, FarmRosters } from './FarmPanels.js';
import { managerShort } from '../domain/identity.js';
import { LEAGUE_2026 } from '../domain/config.js';
import {
  BUNDLED_PROSPECTS, norm, searchProspects, type Prospect, type ProspectBank,
} from '../domain/prospectBank.js';
import {
  atLeast,
  type DraftState, type FaabEntry, type FarmNomination, type FarmTarget, type GmId,
  type LeagueSettings,
  type LeagueStore, type Role,
} from '../store/types.js';
import { shortSurname, splitName } from './BoardGrid.js';
import { farmRoundsNow, type Override } from '../domain/overrides.js';
import { farmRosters } from '../domain/farmHistory.js';
import { Citations } from './RuleText.js';
import { RAIL_1X, RailGrip } from './RailGrip.js';

const SEASON = 2026;

/*
 * WHICH TAB OF THE FARM CARD (Tim, 8 September: "my farm list needs to be fixed to be formatted
 * like the Player Lists on the main draft -- two tabs, plus signs, etc."). Its own key rather
 * than the main draft's `ffl.pqtab.v1`: a man reading the class of 2027 between lots and a man
 * working his picklist on the night are on two screens, and the tab he left one on is not an
 * answer about the other.
 */
const FQ_TAB_KEY = 'ffl.fqtab.v1';

interface Props {
  order: readonly string[];
  actor: string;
  privileged: boolean;
  /**
   * Every cell of the main board is filled, and nobody has marked it done yet.
   *
   * The lock on this screen is `status === 'complete'`, which is a commissioner pressing a button
   * — so on a full board the wait is on a person, not on the picks, and the banner has to say
   * which. Passed rather than derived here because the board is the other screen's to count.
   */
  mainBoardFull?: boolean;
  /** Mark the main draft done, from here. Absent for anybody who may not. */
  onFinishDraft?: () => void;
  /**
   * The full ladder, because "may I bid" and "may I bid for somebody else" are different
   * questions and `privileged` only answers the second. A viewer answers no to both.
   */
  role: Role;
  store: LeagueStore;

  /**
   * Is the manager inside the sandbox?
   *
   * THERE IS NO MOCK FARM AUCTION, and that is the point of this flag. The Farm rounds tab hangs
   * off the same board as the main draft, so switching the mock on and clicking across landed a
   * manager on the REAL auction — live bids, live FAAB, live writes — under a banner reading
   * "nothing is saved". He could win a prospect he was only playing at. So the auction still
   * SHOWS in a mock, because knowing where the bidding stands is exactly what you want while
   * planning, and every control that writes is off.
   */
  sandbox?: boolean;

  /**
   * IS THE ORDER A PROJECTION RATHER THAN A DRAW?
   *
   * The serpentine (2017-II-5 §3) runs off the draft order, and before the lottery is published
   * this screen was handed the CHALK order and had no idea. So the nomination grid drew a real
   * turn sequence — first nomination, thirteenth, twenty-first — against seats nobody had been
   * given yet, and every control on it worked. A prospect nominated in the wrong seat cannot be
   * put back: the pool is one list and the turn has passed.
   *
   * Reason as good as the sandbox's, so it uses the same choke point.
   */
  projected?: boolean;

  /*
   * THE RAIL, AS THE MAIN DRAFT LEFT IT (Tim, 8 September: "the sidebar collapse button should
   * affect the farm rounds view too"). Both come from `DraftBoard`, which owns them for all three
   * boards — the toggle sits in a row above this screen, and a width set on the draftboard that
   * reverted on the way here would be a setting nobody kept using.
   */
  railOpen?: boolean;
  railW?: number;
  onRailW?: (n: number) => void;
}

/** One sale that was put back, and everything needed to hand the hammer down again. */
type FarmRedo = { round: number; slot: number; player: string; winner: string; bid: number };

export function FarmDraft(
  {
    order, actor, privileged: privilegedProp, role, store, sandbox = false, projected = false,
    mainBoardFull = false, onFinishDraft,
    railOpen = true, railW = RAIL_1X, onRailW,
  }: Props,
) {
  /*
   * TWO REASONS, ONE STATE. A mock and an undrawn lottery are different facts about the evening
   * and the same fact about this screen: everything shows, nothing writes. Combining them here
   * rather than at each of the dozen controls is what stops the next one being missed — which is
   * the argument the sandbox flag already makes, one line down.
   */
  const readOnly = sandbox || projected;
  /*
   * ONE CHOKE POINT for the sandbox, deliberately shadowing the prop.
   *
   * `privileged` gates about a dozen separate things in here — bidding for somebody else, the
   * going-gone timer, undoing a sale, the round reset — and switching them off one at a time is
   * how you miss one. A commissioner inside a mock is, for the purposes of this screen, not a
   * commissioner: he is looking, not running the auction.
   */
  const privileged = privilegedProp && !readOnly;
  /*
   * PER-GM BIDDING. Every raise and every pass used to require `privileged`, which on draft night
   * means one man typing what ten men shout — and the app then recorded each of those bids under
   * HIS name until the `by` argument was added to `guardRaise`. Now each GM enters his own.
   *
   * The commissioner keeps every power he had, including bidding on somebody's behalf, because
   * somebody is always on a phone in a car. What changes is that he is no longer the only path.
   *
   * A VIEWER MAY NOT BID. He holds no team and takes no action — the same line drawn in
   * `firestore.rules`, where `linked()` refuses him — so `atLeast(role, 'manager')` and not a
   * mere check that he is signed in.
   */
  const canBid = atLeast(role, 'manager') && !readOnly;
  /** May I act on this man's row? My own if I am a manager; anybody's if I am a commissioner. */
  const canBidFor = (who: string) =>
    privileged || (canBid && who.toUpperCase() === actor.toUpperCase());

  const [log, setLog] = useState<FarmNomination[]>([]);
  const [bank, setBank] = useState<ProspectBank>(BUNDLED_PROSPECTS);
  const [query, setQuery] = useState('');
  /*
   * The sold card's dwell (13c). One second is enough resolution and the timer only runs while a
   * sale is fresh, so it costs nothing between nominations.
   */
  const [ledger, setLedger] = useState<FaabEntry[]>([]);
  const [settings, setSettings] = useState<LeagueSettings | null>(null);
  /**
   * How many rounds this auction runs.
   *
   * READ FROM THE STORE, NOT FROM A PROP. It was `rounds = 2` with a default and no caller — the
   * same shape as three other bugs in this app, where the value that mattered was never passed
   * and the default quietly stood in for it. Two rounds is the 2025 experiment and it lapses
   * unless the committee re-votes on 26 August; a commissioner sets the answer on the
   * configuration table and it lands here without a redeploy.
   */
  const [overrides, setOverrides] = useState<Override[]>([]);
  useEffect(() => {
    let live = true;
    const load = () => void store.overrides(SEASON).then((o) => { if (live) setOverrides(o); });
    load();
    const off = store.subscribe(SEASON, (w) => { if (w === 'overrides') load(); });
    return () => { live = false; off(); };
  }, [store]);
  const rounds = farmRoundsNow(overrides, 2);
  /*
   * The same on/off switch the live board uses. `Pause the clock` writes `setup`, so anything
   * self-terminating has to read this — the going-gone clock most of all.
   */
  const [draft, setDraft] = useState<DraftState | null>(null);
  /** What a write was refused for, shown where the button is rather than swallowed. */
  const [refused, setRefused] = useState<string | null>(null);
  /** The round reset, which is the one control here nothing else on the screen can undo. */
  const [resetting, setResetting] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [wiping, setWiping] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  /**
   * THE SIXTH READER (17g). The record keeps only the standing high bid, so "was I in this and
   * have I been passed" is not a fact the store can answer. This phone remembers its own last
   * raise on the open lot; with the high bidder that is enough to make OUTBID a state.
   */
  const [myBidOn, setMyBidOn] = useState<{ round: number; slot: number; amount: number } | null>(null);

  /**
   * WHAT REDO REMEMBERS: a sale that was put back, and the price that won it.
   *
   * This tab's, per season, in `sessionStorage` — the board's redo stack is built the same way and
   * for the same reason. A redo stack is one person's short-term memory of what he just did;
   * shared between two commissioners it would let one hand back a sale the other reversed on
   * purpose. It survives a refresh and dies with the tab, which is the right lifetime for it.
   */
  const REDO_KEY = `ffl.farmredo.${SEASON}`;
  const [redo, setRedo] = useState<FarmRedo[]>(() => {
    try {
      const raw = sessionStorage.getItem(REDO_KEY);
      const v: unknown = raw ? JSON.parse(raw) : null;
      return Array.isArray(v) ? (v as FarmRedo[]) : [];
    } catch { return []; }
  });
  useEffect(() => {
    try { sessionStorage.setItem(REDO_KEY, JSON.stringify(redo)); } catch { /* private mode */ }
  }, [REDO_KEY, redo]);

  useEffect(() => {
    let live = true;
    const load = () => Promise.all([
      store.farmNominations(SEASON), store.prospectBank(SEASON), store.faab(SEASON),
      store.settings(SEASON), store.draftState(SEASON),
    ]).then(([n, pb, f, st, d]) => {
      if (!live) return;
      setLog(n); if (pb) setBank(pb); setLedger(f); setSettings(st); setDraft(d);
    });
    void load();
    const off = store.subscribe(SEASON, (what) => {
      if (what === 'farmNominations' || what === 'prospectBank' || what === 'faab'
        || what === 'settings' || what === 'draftState') void load();
    });
    return () => { live = false; off(); };
  }, [store]);

  /** Every turn in the two rounds, serpentine, with whatever has been recorded against it. */
  const turns = useMemo(() => {
    const out: Turn[] = [];
    for (let round = 1; round <= rounds; round++) {
      const seats = round % 2 === 1 ? order : [...order].reverse();
      seats.forEach((gm, i) => {
        out.push({
          round,
          slot: i + 1,
          nominator: gm,
          rec: log.find((n) => n.round === round && n.slot === i + 1),
        });
      });
    }
    return out;
  }, [order, rounds, log]);

  /*
   * THE AUCTION'S OWN CLOCK, STARTED AND STOPPED BY A PERSON (Tim, 8 September: "need start/pause,
   * reset pick, reset draft, undo/redo controls on the farm rounds just like the main draft").
   *
   * This used to read `draft?.status !== 'live'` — the MAIN draft's state — which made the screen
   * contradict itself: the farm only unlocks at `status === 'complete'`, so in every state where
   * bidding was legal `paused` was true, the auto-hammer returned at its own guard, and the
   * going-gone clock counted down in front of the room and never once sold a lot.
   *
   * `farmPausedAt` is the instant the room stopped, and it doubles as the frozen `now` every clock
   * on this screen reads — the same trick the board uses, so a pause freezes the figures rather
   * than letting them run on behind a disabled button.
   */
  const farmPausedAt = useMemo(() => {
    const open = (draft?.farmPauses ?? []).find((x) => !x.until);
    const t = open ? Date.parse(open.at) : NaN;
    return Number.isNaN(t) ? null : t;
  }, [draft?.farmPauses]);
  const farmLive = draft?.farmStatus === 'live';
  const paused = !farmLive;
  /** Every clock on this screen reads this, so stopping the room stops the numbers. */
  const clockNow = farmPausedAt ?? now;

  /*
   * THE FARM ROUNDS COME AFTER THE DRAFT, and now the app enforces that rather than assuming it.
   *
   * A farm player occupies a keeper slot, and what a GM will pay for one depends entirely on what
   * his sixteen rounds actually produced — so an auction opened halfway through the main draft is
   * being bid in the dark. Worse, on the night the two screens are one tab apart: the farm pane
   * was reachable, and enterable, from the first pick onwards.
   *
   * `complete` is the commissioner saying the draft is done. Until he does, this screen reads.
   */
  const draftDone = draft?.status === 'complete';
  const locked = !draftDone;

  /*
   * ONE DEFINITION OF "WHOSE NOMINATION IS NEXT" (Tim, 9 September: "i can't get the farm pick
   * rounds to reset fully to john..."). This asked `!t.rec` while `auctionView` asked whether a
   * player had been named, so after a reset the bar said John and this card said Samant — the
   * same screen disagreeing with itself about the same turn. `unstarted` is the domain's, and it
   * is now the only copy.
   */
  const current = turns.find(unstarted);
  const { sold } = nomSoldCount(turns);
  /*
   * OWNED IS NOT AVAILABLE (Tim, 7 September: "make sure there are no players already on a farm
   * team showing up on the farm player index").
   *
   * `taken` was tonight's nominations alone, so Jeremiah Smith — bought by Matt in 2024 and still
   * on his farm roster — sat at the top of the board as though he could be had again. A farm
   * player stays owned until he reaches the NFL or is released, which is exactly what `active`
   * means in the sales history, so the pool takes the held rows out as well as tonight's.
   */
  const heldNow = useMemo(
    () => farmRosters(order).flatMap((r) => r.held.map((h) => h.player)),
    [order],
  );
  const taken = useMemo(
    () => [...log.filter((n) => n.player).map((n) => n.player!), ...heldNow],
    [log, heldNow],
  );

  const held = useMemo(() => {
    const m: Record<string, number> = {};
    for (const n of log) if (n.winner) m[n.winner] = (m[n.winner] ?? 0) + 1;
    return m;
  }, [log]);

  const results = useMemo(
    () => searchProspects(bank, query, { taken, positions: FANTASY_POSITIONS }).slice(0, 16),
    [bank, query, taken],
  );

  /* ================================ THE FARM PICKLIST ========================================
   *
   * "create a pick list function for the farm draft section too" (Tim, 6 September).
   *
   * The main board has one — star a man off the ranking, keep him in an ordered private list,
   * and have the list say who is still there. The farm rounds are where a list is worth MORE,
   * because there is no consensus board in the room and nobody can hold thirty college names in
   * his head at nine o'clock, and this screen had nothing: the prospect list was drawn only on
   * your own turn, so the twenty minutes you spend watching other men nominate — the twenty
   * minutes you would actually use to build a list — offered you nothing to do.
   *
   * IT IS THE LIST HE ALREADY HAS. `FarmTargetList` is the Farm targets card on the home page,
   * private in `firestore.rules` from everybody including a commissioner, with no plural reader
   * anywhere in the store. One list, two screens: a man tags Fannin in August at home and finds
   * him at the top of the rail in November. Nothing new is stored and nothing new is exposed.
   *
   * ORDER IS THE ARRAY'S ORDER, which is what the home card was already writing — so the arrows
   * cost no schema change. It is a running order for HIS OWN nominations, not a bid and not a
   * price: `FarmTarget` still carries neither, for the reason written on the type.
   */
  const [targets, setTargets] = useState<FarmTarget[]>([]);
  const targetsRef = useRef<FarmTarget[]>([]);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      const l = await store.farmTargetsFor(SEASON, actor as never);
      if (!alive) return;
      targetsRef.current = l?.targets ?? [];
      setTargets(targetsRef.current);
    };
    void load();
    const off = store.subscribe(SEASON, (what) => { if (what === 'farmTargets') void load(); });
    return () => { alive = false; off(); };
  }, [store, actor]);

  /** Every change goes through here: paint at once, then write. */
  const editTargets = useCallback((fn: (prev: FarmTarget[]) => FarmTarget[]) => {
    const next = fn(targetsRef.current);
    if (next === targetsRef.current) return;
    targetsRef.current = next;
    setTargets(next);
    void store.saveFarmTargets({
      season: SEASON, gm: actor as never, targets: next, updatedAt: new Date().toISOString(),
    });
  }, [store, actor]);

  const watched = useMemo(() => new Set(targets.map((t) => norm(t.player))), [targets]);

  const addTarget = useCallback((p: Prospect) => {
    editTargets((prev) => (prev.some((t) => norm(t.player) === norm(p.n)) ? prev : [...prev, {
      player: p.n, position: p.p, school: p.s, addedAt: new Date().toISOString(),
    }]));
  }, [editTargets]);

  const dropTarget = useCallback((player: string) => {
    editTargets((prev) => prev.filter((t) => norm(t.player) !== norm(player)));
  }, [editTargets]);

  const moveTarget = useCallback((from: number, to: number) => {
    editTargets((prev) => {
      if (from === to || from < 0 || to < 0 || from >= prev.length || to >= prev.length) return prev;
      const next = [...prev];
      const [row] = next.splice(from, 1);
      if (!row) return prev;
      next.splice(to, 0, row);
      return next;
    });
  }, [editTargets]);

  /*
   * WHO HAS GONE, FROM THE PUBLIC LOG AND NOWHERE ELSE. A row strikes through when the room has
   * him — watching is over at that point, the auction has him and what happens next is bidding.
   * The home card derives it the same way, and neither reads anybody else's list to do it.
   */
  const nominatedBy = useMemo(() => {
    const out = new Map<string, string>();
    for (const n of log) if (n.player) out.set(norm(n.player), n.winner ?? n.nominator);
    return out;
  }, [log]);

  /*
   * THE BOARD IN THE RAIL: its own field and its own chips, independent of the auction card's
   * search. That one exists to open a lot and is emptied every time somebody does; this one is
   * for reading the class of 2027 at leisure while nine other men take their turns.
   */
  /*
   * THE MAN CHOSEN, BEFORE HE IS NOMINATED (Tim, 7 September). A tap on a row in the panel used
   * to open the bidding on him in front of the room; it now stands him on the card, where the
   * Nominate button is the thing that tells the league.
   */
  const [chosen, setChosen] = useState<Prospect | null>(null);
  /*
   * The board is the default tab, because on a night with nothing on the block the question is
   * who there is — the watchlist is what you already know. It survives a reload, so a man who
   * works off his own list all evening is not put back on the board every time the page comes up.
   */
  const [fqTab, setFqTab] = useState<'board' | 'mine'>(() => {
    try { return localStorage.getItem(FQ_TAB_KEY) === 'mine' ? 'mine' : 'board'; } catch { return 'board'; }
  });
  const showFqTab = (t: 'board' | 'mine') => {
    setFqTab(t);
    try { localStorage.setItem(FQ_TAB_KEY, t); } catch { /* private mode */ }
  };
  const [fquery, setFquery] = useState('');
  const [fpos, setFpos] = useState<string[]>([]);
  const [hsOnly, setHsOnly] = useState(false);
  const poolAll = useMemo(
    () => searchProspects(bank, '', { taken, positions: FANTASY_POSITIONS }),
    [bank, taken],
  );
  const pool = useMemo(() => searchProspects(bank, fquery, {
    taken,
    positions: fpos.length ? fpos : FANTASY_POSITIONS,
  }).filter((p) => (hsOnly ? Boolean(p.hs) : true)),
  [bank, fquery, fpos, hsOnly, taken]);

  /** The bank row behind a name on the list, so nominating from it records position and school. */
  const prospectOf = useCallback(
    (player: string) => bank.prospects.find((x) => norm(x.n) === norm(player)),
    [bank],
  );

  const mine = current && current.nominator.toUpperCase() === actor.toUpperCase();

  /*
   * MY OWN right to bid on the open lot, which is three separate questions and used to be one.
   * A dead button with no reason is the thing draft night cannot afford — somebody will tap it
   * four times and then say the app is broken.
   */
  const myFarmFull = (held[actor.toUpperCase()] ?? 0) >= LEAGUE_2026.maxFarmPlayers;
  const whyNotBid = !canBid
    ? 'You hold no team, so there is nothing to bid with'
    : locked
      ? 'The farm rounds open when the main draft is complete'
      : myFarmFull
        ? `Your farm is full at ${LEAGUE_2026.maxFarmPlayers} — 2017-II-5 §6`
        : undefined;
  const canAct = Boolean(current) && (mine || privileged);

  /* -- the live auction (13a/13c) --------------------------------------------------------- */

  const view = useMemo(
    () => auctionView({
      turns, me: actor, heldBy: held, cap: LEAGUE_2026.maxFarmPlayers,
    }),
    [turns, actor, held],
  );

  const soldNow = useMemo(() => {
    const t = justSold(turns, now);
    if (!t?.rec?.winner || t.rec.bid == null) return null;
    // The drawing counts the dwell down on the card — "next up in 0:06" — so the pause reads as
    // deliberate rather than as the app having stopped.
    const left = Math.max(0, Math.ceil((SOLD_DWELL_MS - (now - Date.parse(t.rec.updatedAt))) / 1000));
    const w = t.rec.winner.toUpperCase();
    const after = turns.find(unstarted);
    return {
      player: t.rec.player ?? '', winner: t.rec.winner, bid: t.rec.bid, left,
      winnerFarm: { held: held[w] ?? 0, cap: LEAGUE_2026.maxFarmPlayers },
      winnerBudget: budgetOf(w) - t.rec.bid,
      next: after?.nominator ?? null,
    };
  }, [turns, now, held, ledger]);

  /**
   * What the viewer has to spend. Opening balance plus the ledger, the same derivation the slate
   * screen uses — so the number on the bid card cannot disagree with the number on his keepers.
   * Tonight's own farm wins are in the ledger only once a commissioner records them, so the
   * "left if you win" figure is what it says: a projection of this bid, not of the whole night.
   */
  function budgetOf(who: string) {
    const gm = who.toUpperCase();
    const moved = ledger
      .filter((e) => e.gm?.toUpperCase() === gm)
      .reduce((n, e) => n + e.amount, 0);
    return openingBalance(gm) + moved;
  }
  const faabLeft = useMemo(() => budgetOf(actor), [actor, ledger]);

  /** The open nomination, which is what a raise or a pass is written against. */
  const live = turns.find((t) => t.rec?.player && !t.rec.winner && !t.rec.passed) ?? null;

  /**
   * Everyone already out of the round in front of us — 2025-proposal-04-farm-two-rounds §5, §7.
   *
   * The round is the LIVE lot's if there is one, otherwise the next nomination's, because between
   * lots the question the screen is answering is about the turn that is coming. A man out of round
   * one is back in for round two, which is the whole difference between this and a full farm.
   */
  const roundNow = live?.round ?? turns.find(unstarted)?.round ?? 1;
  const wonThisRound = useMemo(() => roundWinners(turns, roundNow), [turns, roundNow]);

  /**
   * The going-gone clock. Tim's call: a standing bid that nobody challenges SELLS. It is the one
   * clock in the app that decides anything, because an auction that never terminates is not an
   * auction — and it counts from the LAST RAISE, so two men trading bids extend the lot instead of
   * racing a fixed deadline.
   */
  /**
   * How long the room has been stopped since this bid landed.
   *
   * PAUSING HAS TO STOP THE CLOCK, NOT JUST ITS PICTURE (Tim, 9 September: "when I pause the farm
   * clock, it seems to run in the background. when I hit start again, the clock zeroes out and the
   * player is awarded to whoever was leading before the pause").
   *
   * Freezing `clockNow` held every figure still and kept the hammer from firing, which looked like
   * a pause. But `bidAt` is a wall-clock instant, so the moment the room started again and `now`
   * became real, the twenty seconds had been spent by the pause itself: the clock read 0:00 on the
   * first tick and the lot sold to whoever was leading when Tim stopped the room. A pause called to
   * settle an argument decided it.
   *
   * `farmPauses` has been written on every pause since the controls shipped and nothing read it.
   * Now the clocks count AUCTION time — wall time with these intervals taken out — which is the
   * model the draft board has always used.
   */
  const heldOnBid = useMemo(
    () => heldSince(draft?.farmPauses, live?.rec?.bidAt, clockNow),
    [draft?.farmPauses, live?.rec?.bidAt, clockNow],
  );

  const sellClock = useMemo(
    () => countdown(live?.rec?.bidAt, settings?.clocks?.bidSellsAfterSeconds, clockNow, heldOnBid),
    [live, settings, clockNow, heldOnBid],
  );

  /**
   * How long the man who is up has left to name somebody. Advisory — nothing nominates for
   * anybody, because a farm player forced on a GM is worse than a slow auction. Counted from when
   * the previous lot closed, which is the only "start" the data has.
   */
  const nominateClock = useMemo(() => {
    if (live) return null;
    const closed = log
      .filter((n) => n.updatedAt)
      .map((n) => n.updatedAt)
      .sort()
      .pop();
    /* Auction time here too, or a pause between lots eats the nominator's minute. */
    return countdown(closed, settings?.clocks?.nominateSeconds, clockNow,
      heldSince(draft?.farmPauses, closed, clockNow));
  }, [live, log, settings, clockNow, draft?.farmPauses]);

  /**
   * THE SAME STANDING, IN NOMINATION ORDER (Tim, 8 September: "give me a vertical table
   * representing all GMs vertically ... clearly identify in real-time who's leading the bids with
   * what number bid").
   *
   * `bidders` above is the rail's list and it sorts by bid, which is right for reading it. This
   * one must not: a commissioner types into these rows, and a table that reorders itself between
   * his eye and his finger records the wrong man's bid. So the order is the nomination order all
   * evening and the leader is marked in place.
   */
  const bidRows = useMemo<BidRow[]>(() => {
    const passed = new Set((live?.rec?.passedBy ?? []).map((g) => g.toUpperCase()));
    return order.map((g) => {
      const gm = g.toUpperCase();
      const block = farmBlock(gm, held, LEAGUE_2026.maxFarmPlayers, wonThisRound);
      return {
        gm: g,
        full: Boolean(block),
        /* The two blocks read differently, and a man told the wrong one argues about it. */
        blockedWhy: block
          ? (block.why === 'Your farm is full.'
            ? `${LEAGUE_2026.maxFarmPlayers} held — 2025-proposal-04-farm-two-rounds §10`
            : 'Won this round — 2025-proposal-04-farm-two-rounds §7')
          : undefined,
        passed: passed.has(gm),
        left: budgetOf(gm),
      };
    });
  }, [order, live, held, ledger, wonThisRound]);

  /*
   * When the going-gone clock expires the lot sells itself. Exactly one client may do the write —
   * a commissioner's — because ten browsers all noticing the same expiry would all write, and the
   * guard would refuse nine of them noisily. It is idempotent either way: `guardSell` refuses a
   * second sale.
   */
  useEffect(() => {
    if (!privileged || !live?.rec?.bidAt) return;
    /*
     * PAUSED MEANS PAUSED. The going-gone clock used to keep running through an intermission and
     * through a commissioner stopping the room to settle an argument — so a lot could sell itself
     * while nobody was looking at it, which is the one thing a clock must never do.
     */
    if (paused) return;
    /*
     * THE CLOCK DECIDES WHETHER ANYTHING HAPPENS; THE BIDS DECIDE WHAT.
     *
     * Asked the other way round — the empty-lot branch first — a lot closed the instant it was
     * nominated, because a lot nominated by a man out of his own round has no bid on it a
     * millisecond after it opens. The room never got its twenty seconds to take him at ₣1, which is
     * the exact chance the ruling turns on.
     *
     * The same `held` the visible clock used, or the two would disagree about when it ends.
     */
    if (!bidHasSold(live.rec.bidAt, settings?.clocks ?? null, clockNow, heldOnBid)) return;
    /*
     * Out of time with nothing on it. `guardSell` refuses a sale with no bidder, so this is the
     * other ending: the turn closes with the player STILL ON IT, which is what takes him off the
     * board for the rest of the evening (2025-proposal-04-farm-two-rounds §7, and Tim's ruling that
     * he "is not able to be brought up as a nominee by another GM later").
     */
    if (!live.rec.highBidder || live.rec.highBid == null) { void closeLot(); return; }
    void hammer();
  }, [privileged, live, settings, clockNow, paused, heldOnBid]);

  /** A raise writes the standing bid; only a commissioner may, per the Firestore rules. */
  /**
   * Every write re-reads the document first. Two people hold these rights at once, the store has
   * no compare-and-set, and last-write-wins would let a stale client undo a bid — see the note
   * above the guards in `domain/farmAuction.ts`.
   */
  async function fresh(round: number, slot: number) {
    const all = await store.farmNominations(SEASON);
    return all.find((n) => n.round === round && n.slot === slot);
  }

  /*
   * ONE PLACE THAT WRITES A NOMINATION (Tim, 22 August).
   *
   * Nine call sites below save one, and every one of them had to remember to say who was typing —
   * which none of them did, so the record only ever knew whose nomination it WAS. Draft night is
   * one commissioner entering nominations and bids for a room, so `nominator` is frequently not
   * the man at the keyboard, and the activity log read "John nominated a prospect" for a row Tim
   * had entered. Wrapping the store call means the tenth call site cannot forget either.
   *
   * `actor` and not `gm`: the real account, so that a commissioner who is ALSO impersonating
   * somebody is recorded as himself.
   */
  const saveNomination = (n: FarmNomination) =>
    store.saveFarmNomination({ ...n, updatedBy: actor.toUpperCase() });

  async function raise(to: number, bidder: string = actor) {
    if (locked) return;
    if (!live?.rec || !canBidFor(bidder)) return;
    const now_ = await fresh(live.round, live.slot);
    /* Full farm, or already won this round — both put him out of this lot. */
    const blocked = Boolean(farmBlock(
      bidder, held, LEAGUE_2026.maxFarmPlayers, wonThisRound,
    ));
    const g = guardRaise(now_, to, blocked, bidder);
    if (!g.ok) { setRefused(g.why); return; }
    setRefused(null);
    if (bidder.toUpperCase() === actor.toUpperCase()) {
      setMyBidOn({ round: live.round, slot: live.slot, amount: to });
    }
    await saveNomination({
      ...now_!, highBid: to, highBidder: bidder.toUpperCase(),
      bidAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  }

  /** Passing is remembered — it is a deliberate exit and the rail shows it. */
  async function passBid(gm: string = actor) {
    if (locked) return;
    if (!live?.rec || !canBidFor(gm)) return;
    const now_ = await fresh(live.round, live.slot);
    if (!now_?.player || now_.winner) return;
    // Merges rather than refuses: a lost append costs a name in a column and nothing else.
    const next = withPass(now_, gm);
    if (next === now_) return;
    await saveNomination(next);
  }

  /** And back in, for the man who says "no, wait". */
  async function unpass(gm: string) {
    if (locked) return;
    if (!live?.rec || !canBidFor(gm)) return;
    const now_ = await fresh(live.round, live.slot);
    if (!now_?.player || now_.winner) return;
    await saveNomination(withoutPass(now_, gm));
  }

  /* -- putting a lot back ---------------------------------------------------------------------
   *
   * Draft night is ten people talking over each other and one commissioner typing. The write
   * guards stop two clients corrupting a lot; these are for a lot that is perfectly consistent and
   * simply WRONG — the wrong man named, thirty heard for thirteen, a hammer while a hand was up.
   * Nothing here deletes a turn: a cleared nomination is an unstarted turn again.
   *
   * NONE OF THEM CHECKS `locked`, AND THAT IS THE FIX.
   *
   * They all did, which meant the commissioner's undo was switched off by the same gate that
   * stops managers bidding — so a nomination entered while the board was open became permanent
   * the moment the board closed, with nothing in the app able to touch it. Exactly the state Tim
   * found: a 2026 farm player sitting on his roster and no way to take it off.
   *
   * The lock exists to stop the ROOM acting before the main draft is done. It was never meant to
   * stop the one person whose job is cleaning up after it, and the two ideas had been conflated
   * into one boolean. A commissioner can always put a lot back.
   */

  /**
   * Clear the bidding and put the lot back where the nomination left it.
   *
   * BACK TO THE NOMINATOR'S ₣1, not back to nothing (Tim, 9 September: "i was able to clear bids
   * and get it back to where it says nominated by Samant, but he doesn't have any FAAB at stake").
   * Clearing the bids means undoing what the room did, and what the room did starts after the
   * opening stake — wiping that too leaves a lot on the board that nobody has bid on, which is not
   * a state a named player can be in.
   */
  async function resetBids() {
    if (!live?.rec || !privileged) return;
    const now_ = await fresh(live.round, live.slot);
    if (!now_) return;
    setRefused(null);
    await saveNomination(withOpeningBid(clearBids(now_), opener(now_.nominator)));
  }

  async function takeBack() {
    if (!live?.rec || !privileged) return;
    const now_ = await fresh(live.round, live.slot);
    if (!now_) return;
    setRefused(null);
    await saveNomination(clearNomination(now_));
  }

  /**
   * Clear the whole round.
   *
   * Written turn by turn rather than as a bulk delete, because the store has no delete for a
   * nomination and should not grow one for this: a cleared turn is an UNSTARTED turn, which is a
   * state the rest of the screen already understands, and leaving the documents in place keeps
   * the turn order and everything else derived from it intact.
   */
  async function resetRound() {
    if (!privileged || wiping) return;
    setWiping(true);
    try {
      for (const t of turns) {
        if (!t.rec) continue;
        const now_ = await fresh(t.round, t.slot);
        if (now_) await saveNomination(clearNomination(undoSale(now_)));
      }
      setRefused(null);
      setResetting(false);
      setConfirm('');
    } finally {
      setWiping(false);
    }
  }

  /** Undo the most recent sale. The player goes back up at the price that won him. */
  async function unsell(t: Turn) {
    if (!privileged || !t.rec?.winner) return;
    const now_ = await fresh(t.round, t.slot);
    if (!now_?.winner) return;
    setRefused(null);
    await saveNomination(undoSale(now_));
    await unspend(now_.round, now_.slot);
  }

  /**
   * Take a farm win's money back off the ledger.
   *
   * Derived from the TURN, not searched for by amount: undoing a ₣12 sale must not take somebody
   * else's ₣12 off. And it is fire-and-forget — a sale that is already put back with its row still
   * there is a ledger the FAAB page can fix, where a failed undo that refuses to release the lot is
   * a room that cannot carry on.
   */
  async function unspend(round: number, slot: number) {
    await store.deleteFaab(SEASON, farmSpendId(SEASON, round, slot)).catch(() => {});
  }

  /**
   * The clock ran out and nobody had bid. The lot ends unsold.
   *
   * Fire-and-forget and idempotent: `closeUnsold` on a turn that is already closed writes the same
   * thing, and a failure leaves a lot the commissioner can pass by hand.
   */
  async function closeLot() {
    if (!privileged || !live?.rec) return;
    const now_ = await fresh(live.round, live.slot);
    if (!now_ || now_.winner || now_.passed || now_.highBidder) return;
    await saveNomination(closeUnsold(now_));
  }

  /** The hammer: the standing high bid becomes the sale. */
  async function hammer() {
    if (locked) return;
    if (!live?.rec || !privileged) return;
    const now_ = await fresh(live.round, live.slot);
    const g = guardSell(now_);
    if (!g.ok) { setRefused(g.why); return; }
    setRefused(null);
    const at = new Date().toISOString();
    const sold = { ...now_!, winner: now_!.highBidder, bid: now_!.highBid, updatedAt: at };
    await saveNomination(sold);
    /*
     * AND THE MONEY MOVES WITH IT (Tim, 9 September: "FAAB should update immediately and be
     * recorded to ledger when bid is complete"). The row's id is the turn, so pressing the hammer
     * twice, or two commissioners pressing it at once, writes one row — and `budgetOf` reads the
     * ledger, so the "left" column in the bid table drops the moment the lot sells rather than
     * showing last night's figure to the man deciding his next raise.
     */
    const row = farmSpendEntry(sold, actor.toUpperCase() as GmId, at);
    if (row) await store.saveFaab(row).catch(() => {});
  }

  /* -- the room's own controls, the board's four, in the board's order ------------------------ */

  /**
   * START, PAUSE, REOPEN — the same switch the board has, writing the auction's own ledger.
   *
   * A pause is an interval and not a flag, for the reason the draft's is: the clocks count AUCTION
   * time, wall time minus these intervals, so a stop of any length leaves every standing bid
   * exactly where it was. Append-only, and the open entry is the one with no `until`.
   */
  async function setFarmStatus(status: NonNullable<DraftState['farmStatus']>) {
    if (!draft || !privileged || locked) return;
    const now_ = new Date().toISOString();
    const pauses = [...(draft.farmPauses ?? [])];
    const open = pauses.length && !pauses[pauses.length - 1]!.until ? pauses.length - 1 : -1;
    const stopping = status !== 'live' && draft.farmStatus === 'live';
    const starting = status === 'live' && draft.farmStatus !== 'live';
    if (stopping && open < 0) pauses.push({ at: now_, by: actor.toUpperCase() as never });
    if (starting && open >= 0) pauses[open] = { ...pauses[open]!, until: now_ };
    await store.saveDraftState({
      ...draft,
      farmStatus: status,
      farmPauses: pauses,
      ...(starting && !draft.farmStartedAt
        ? { farmStartedAt: now_, farmStartedBy: actor.toUpperCase() } : {}),
      updatedAt: now_,
    });
  }

  /**
   * RESET THE LOT — the farm's "reset pick": give whoever the room is waiting on his full time
   * back, without stopping the auction.
   *
   * Which clock that is depends on where the lot is. With a bid standing it is the going-gone
   * clock, which counts from `bidAt`; with nobody named yet it is the nomination clock, which
   * counts from the newest `updatedAt` in the log. Both are restarted by writing the row, which is
   * why this touches the record rather than a separate restart stamp: on this screen the timestamp
   * IS the clock.
   */
  async function restartLot() {
    if (!privileged || locked) return;
    const t = live ?? current;
    if (!t) return;
    const now_ = await fresh(t.round, t.slot);
    const at = new Date().toISOString();
    if (now_) {
      await saveNomination({ ...now_, ...(now_.bidAt ? { bidAt: at } : {}), updatedAt: at });
      return;
    }
    /* No row yet: the nomination clock reads the log's newest stamp, so give it a fresh one. */
    await saveNomination({
      season: SEASON, round: t.round, slot: t.slot, nominator: t.nominator,
      passed: false, updatedAt: at,
    });
  }

  /**
   * UNDO AND REDO, over the farm's unit of record — a SALE.
   *
   * The board's undo clears the last pick; here the equivalent act is putting the last sale back
   * up, because a sale is what this screen produces. `undoSale` already says what that means —
   * "the player goes back up for bid at the price that won him" — so undo is that, aimed at the
   * most recent one, and redo hands the hammer back down.
   *
   * The stack is this tab's, in `sessionStorage`, exactly like the board's: a redo stack is one
   * person's short-term memory of what he just did, and sharing it between two commissioners would
   * let one put back a sale the other deliberately reversed. Undo needs no stack at all — the last
   * sale is a fact about the log, readable by anyone.
   */
  const lastSale = useMemo(() => {
    let best: Turn | null = null;
    for (const t of turns) {
      if (!t.rec?.winner || !t.rec.updatedAt) continue;
      if (!best || t.rec.updatedAt > best.rec!.updatedAt) best = t;
    }
    return best;
  }, [turns]);

  async function undoLastSale() {
    if (!privileged || locked || !lastSale?.rec) return;
    const now_ = await fresh(lastSale.round, lastSale.slot);
    if (!now_?.winner) { setRefused('That sale has already been put back.'); return; }
    setRefused(null);
    setRedo((prev) => [{
      round: now_.round, slot: now_.slot, player: now_.player ?? '',
      winner: now_.winner!, bid: now_.bid ?? 0,
    }, ...prev]);
    await saveNomination(undoSale(now_));
    await unspend(now_.round, now_.slot);
  }

  async function redoLastSale() {
    const top = redo[0];
    if (!privileged || locked || !top) return;
    const now_ = await fresh(top.round, top.slot);
    /*
     * The lot may have moved on — sold to somebody else, or the player taken off it. Keep the
     * entry and say so, rather than writing a name back over whatever is there now.
     */
    if (!now_ || now_.winner || now_.player !== top.player) {
      setRefused(now_?.winner
        ? `${nomCode(top.round, top.slot)} has been sold again since; there was nothing to `
          + 'put back.'
        : `${nomCode(top.round, top.slot)} is not ${top.player || 'that player'} any more.`);
      return;
    }
    setRefused(null);
    setRedo((prev) => prev.slice(1));
    const at = new Date().toISOString();
    const sold = { ...now_, winner: top.winner, bid: top.bid, updatedAt: at };
    await saveNomination(sold);
    /* Redo puts the money back too, or the hammer and the undo would not be each other's inverse. */
    const row = farmSpendEntry(sold, actor.toUpperCase() as GmId, at);
    if (row) await store.saveFaab(row).catch(() => {});
  }

  /**
   * Naming a player OPENS the bidding — it does not sell him. That split is the whole shape of
   * turn 13a: the nomination belongs to whoever is up, the sale belongs to the room. The old
   * version wrote nominator, winner and price in one go, which meant somebody had to know the
   * answer before the auction had happened.
   */
  async function nominate(player: string | null, prospect?: Prospect) {
    if (!current || !canAct || locked) return;
    /*
     * The nominator AND every commissioner may open this lot, so somebody else may have named a
     * player between this screen rendering and this click. Two names cannot merge, so the second
     * one has to lose loudly.
     */
    const now_ = await fresh(current.round, current.slot);
    const g = guardNominate(now_, actor, current.nominator, privileged);
    if (!g.ok) { setRefused(g.why); return; }
    setRefused(null);
    const row = {
      season: SEASON,
      round: current.round,
      slot: current.slot,
      nominator: current.nominator,
      player: player ?? undefined,
      position: prospect?.p,
      school: prospect?.s,
      passed: !player,
      updatedAt: new Date().toISOString(),
    };
    /*
     * A pass opens nothing. A name opens the bidding at ₣1 held by the man who said it — see
     * `withOpeningBid`, which is also where the two cases that cannot hold it are written down.
     */
    await saveNomination(player ? withOpeningBid(row, opener(current.nominator)) : row);
    setQuery('');
  }

  /**
   * May this man hold the ₣1 his own nomination opens at?
   *
   * Not if his farm is full (2019-II-6 §6 puts him out of the bidding) and not if he has nothing to
   * pay it with (2025-proposal-01-faab-farm-bids §1 forbids a bid he cannot cover). Either way he
   * may still
   * nominate; the lot simply opens with the clock running and nothing on it.
   */
  function opener(gm: string): string | null {
    const who = gm.toUpperCase();
    /*
     * AND A MAN WHO HAS ALREADY WON THIS ROUND (2025-proposal-04-farm-two-rounds §7). Tim's
     * ruling: "if a GM wins before their round nomination, they are still able to nominate, but the
     * bidding starts at $0 with no one immediately leading the bid." He names the player; the room
     * bids from ₣1; he is not in it.
     */
    if (farmBlock(who, held, LEAGUE_2026.maxFarmPlayers, wonThisRound)) return null;
    return budgetOf(who) >= OPENING_BID ? who : null;
  }

  /** The bar (13a desktop, 13b phone), and who is still live on the lot under it. */
  const bar = useMemo(() => auctionBar(view, actor), [view, actor]);
  const alive = useMemo(
    () => stillBidding(order, live?.rec?.passedBy, held, LEAGUE_2026.maxFarmPlayers, wonThisRound),
    [order, live, held, wonThisRound],
  );

  if (!order.length) return <p className="note">Publish the draft order first.</p>;

  return (
    <>
    {projected && !sandbox && (
      <div className="deadline warn">
        <span className="ico" aria-hidden="true">▲</span>
        <span>
          <b>Projected order.</b> The lottery has not been published, so the nomination order
          below assumes the draw goes to form (<Citations ids={['2020-II-1']} />) — worst finisher
          nominates first, the Toilet Bowl winner lifted to second. The prospects and the FAAB are
          real; the seats are not, and a nomination cannot be taken back. Nothing can be entered
          until the order is published on <b>Order &amp; lottery</b>.
        </span>
      </div>
    )}
    {sandbox && (
      <div className="deadline warn">
        <span className="ico" aria-hidden="true">■</span>
        <span>
          <b>The farm auction has no mock.</b> Bidding is real money against a real board, so it
          runs in one place only. This is the live auction, shown so you can see where it stands —
          nothing here can be entered while the mock is on. Turn the mock off to bid.
        </span>
      </div>
    )}
    {locked && (
      <div className="deadline warn">
        <span className="ico" aria-hidden="true">■</span>
        <span>
          {/*
            ONE SENTENCE (Tim, 7 September). The banner said the condition, then explained it, then
            reported the draft's status — three ways of saying the same thing, in a box that is
            only up because there is nothing to do yet.

            AND A DIFFERENT SENTENCE ONCE THE BOARD IS FULL (Tim, 9 September: "more prominently
            tell me I need to mark the main draft as complete before the farm rounds can start when
            the board is fully filled out"). With every pick in, "opens once the main draft is
            finished" reads as a statement about the PICKS, and the picks are all there — so the
            room stares at a banner describing a condition it has already met. What it is actually
            waiting on is a button, on the other tab, and now it says so and carries one.
          */}
          {mainBoardFull ? (
            <>
              <b>Every pick is in — mark the main draft done to open the farm rounds.</b>{' '}
              The auction is gated on a commissioner saying the draft is over, which is a
              deliberate press rather than something the last pick does on its own.
            </>
          ) : (
            <b>The farm rounds open once the main draft is finished.</b>
          )}
        </span>
        {mainBoardFull && onFinishDraft && (
          <button type="button" className="btn primary" onClick={onFinishDraft}>
            Mark draft done
          </button>
        )}
      </div>
    )}

    {bar && !locked && (
      <FarmBar
        bar={bar}
        detail={[live?.rec?.position, live?.rec?.school].filter(Boolean).join(' · ') || undefined}
        high={view.high}
        clock={live ? sellClock : nominateClock}
      />
    )}

    {/*
      THE RAIL ON THE LEFT, like every other board in the app (Tim, 7 September). 13a drew this
      screen mirrored — main column first, rail on the right — and it was the only page arranged
      that way, so a man crossing from the draftboard to the farm rounds found the column he acts
      in had swapped sides mid-evening. `.farmcols` now sets the same tracks as `.draftcols`.
    */}
    <div className={`draftcols farmcols${railOpen ? '' : ' norail'}`}
      style={{ '--rail': `${railW}px` } as CSSProperties}>
     {railOpen && (
     <div className="railcol">
      {/*
        13c: one slot, five jobs, and it never moves. The nomination search stays BELOW the card
        rather than inside it, so the card's height does not depend on how many prospects match.
      */}
      <AuctionCard
        view={view}
        me={actor}
        detail={[live?.rec?.position, live?.rec?.school].filter(Boolean).join(' · ') || undefined}
        faabLeft={faabLeft}
        farm={{ held: held[actor.toUpperCase()] ?? 0, cap: LEAGUE_2026.maxFarmPlayers }}
        canAct={canBid && !whyNotBid}
        whyNot={whyNotBid}
        myBid={live && myBidOn && myBidOn.round === live.round && myBidOn.slot === live.slot
          ? myBidOn.amount : null}
        sold={soldNow}
        clock={view.state === 'nominate' || view.state === 'waiting' ? nominateClock : sellClock}
        refused={refused}
        onRaise={(to) => void raise(to)}
        onPass={() => void passBid()}
        onSkipNomination={() => void nominate(null)}
        nominateFor={!live && current && !mine && privileged && !locked ? current.nominator : null}
        query={query}
        onQuery={(q) => { setQuery(q); if (chosen) setChosen(null); }}
        chosen={chosen ? { name: chosen.n, pos: chosen.p, school: chosen.s } : null}
        onClearChosen={() => setChosen(null)}
        /* The matches the removed panel used to list, on the card that owns the field. */
        matches={results.map((r) => ({
          name: r.n, pos: r.p, school: r.s, ...(r.devy ?? r.overall ? { rank: r.devy ?? r.overall } : {}),
        }))}
        onChoose={(name) => {
          const hit = results.find((r) => r.n === name);
          if (hit) { setChosen(hit); setQuery(''); }
        }}
        upNext={current
          ? turns.slice(turns.indexOf(current) + 1, turns.indexOf(current) + 4).map((t) => t.nominator)
          : undefined}
        onNominate={() => {
          /* The chosen man if there is one, otherwise whatever he typed — a name no board
             carries is still a legal nomination, which is why the field takes free text. */
          if (chosen) { void nominate(chosen.n, chosen); setChosen(null); return; }
          if (query.trim()) void nominate(query.trim());
        }}
      />

      {/*
        WHAT HE HAS TO SPEND, ON ITS OWN (Tim, 7 September). It used to ride the sentence that
        says whose nomination is next, which made a standing fact about the reader read as news
        about the auction and pushed that sentence onto two lines. It is the same two numbers all
        evening, so it sits between the card and his list, quiet and in one place.
      */}
      {!locked && (
        <div className="farmstanding">
          <span className="fsbudget">
            <i>Budget</i>
            <b>{faab(faabLeft)}</b>
          </span>
          <span className="fsfarm">
            <i>Farm</i>
            <b>{held[actor.toUpperCase()] ?? 0} of {LEAGUE_2026.maxFarmPlayers}</b>
          </span>
        </div>
      )}

      {/*
        THE ROOM IS ONE LIST NOW (Tim, 9 September: "remove 'who is bidding' and combine any lost
        function into the panel labeled 'on Nate Frazier'. That should be my active bid tracking
        panel and commissioner action panel").

        This is where `Who is bidding` was: the same ten men, sorted by bid, each with a one-amount
        button. The bid table under the board is the same ten men in nomination order with the
        amount typed in, so the screen carried two lists of the room that disagreed about their
        order — and the one a commissioner must not misread is the one he types into. The hammer,
        the lot fixes and the cap rule moved down there with it; the rail keeps the auction card,
        which is the viewer's own bid and nobody else's.
      */}

      {/*
        PROSPECT LISTS, in the rail, under the card that says what is on the block.
        Ordered, private, and the same list as the home page's Farm targets card — see the block
        beside `editTargets`. It sits below the bidding because while a lot is live the question
        on the screen is that lot; between lots, which is most of the evening, this is the top
        card with anything to do on it.
      */}
      <details className="card railsec bestsec picksec farmlist" open>
        {/*
          COLLAPSIBLE, LIKE THE MAIN DRAFT'S (Tim, 6 September). It is a `details` wearing the
          same classes as `My picklist`, so it folds on the same chevron, opens to the same
          summary, and carries the same wash — one object on two screens rather than two things
          that look alike.
        */}
        <summary>
          {/*
            THE CARD IS NAMED FOR WHAT IT HOLDS, NOT FOR ONE OF ITS TABS (Tim, 8 September:
            relabel this card with "Prospect Lists", "All prospects" and "My targets"). It read
            `My farm list` above a tab row whose other tab was the board — so the heading claimed
            one pane and the star it carried belonged to that pane too. The star lives on the tab
            it means now, and the heading says what the card is.
          */}
          <h3 className="striphead">Prospect Lists</h3>
          {/*
            THE STATE OF WHICHEVER LIST IS OPEN, like the main draft's (Tim, 8 September). The
            board says what it is filtered to; the watchlist says that nobody else can see it.
            `private` under a search field the search was not applied to said neither.
          */}
          <span className="railsum">
            {fqTab === 'mine' ? 'private' : (fquery.trim() ? `searching "${fquery.trim()}"` : '')}
          </span>
          <i className="chev" aria-hidden="true" />
        </summary>

        {/*
          TWO LISTS, TWO TABS — the main draft's row, on the main draft's classes, because it is
          the same question: which pane of this card am I looking at. They were stacked, so every
          row the watchlist gained came off the board, and on a phone the board's first row sat
          below the fold whenever a man had four names starred.

          The count rides the watchlist's own label. It is the one number that says the list is
          there at all while you are on the board, and it answers "have I starred him already"
          without a tap.
        */}
        <div className="reftabs pqtabs" role="tablist" aria-label="All prospects and my targets">
          <button type="button" role="tab" aria-selected={fqTab === 'board'}
            className={`reftab${fqTab === 'board' ? ' on' : ''}`}
            onClick={() => showFqTab('board')}>All prospects</button>
          <button type="button" role="tab" aria-selected={fqTab === 'mine'}
            className={`reftab${fqTab === 'mine' ? ' on' : ''}`}
            onClick={() => showFqTab('mine')}>
            <span className="pqstar" aria-hidden="true">&#9733;</span>
            My targets
            <span className="pqcount">{targets.length}</span>
          </button>
        </div>

        {/* -- what I want, in order ------------------------------------------------------- */}
        {fqTab === 'mine' && (
        <div className={`fqblock${readOnly ? ' readonly' : ''}`}>
          {targets.length === 0 ? (
            <p className="note railpad">
              Star a prospect on <b>All prospects</b> and he lands here, in your order. Nobody
              else can see this list — not the other GMs, not a commissioner.
            </p>
          ) : (
            <ol className="fqlist">
              {targets.map((t, i) => {
                const gone = nominatedBy.get(norm(t.player));
                const { last, first } = splitName(t.player);
                return (
                  /*
                    THE SCHOOL RIDES THE TITLE, NOT THE ROW. A 320px rail carrying an index, a
                    name, a position, Nominate, two arrows and an × has about eighty pixels left,
                    and "Ohio State" was taking them off the one thing the row is for. It is on
                    the row's tooltip, and on every row of the board below.
                  */
                  <li key={t.player} className={gone ? 'gone' : ''}
                    title={[t.player, t.position, t.school].filter(Boolean).join(' · ')}>
                    <span className="pqn">{i + 1}</span>
                    {/*
                      THE PLUS LEADS THE ROW, as it does on the main draft's picklist (Tim, 8
                      September). Nominating off this list is the whole point of keeping one: the
                      turn comes round, the top name still standing is the man you decided on an
                      hour ago, and you should not have to read past a position chip and two
                      arrows to say so. Reading order is the order of the act.

                      It is the ranking's button, not a word: `Nominate` set the row's width, and
                      the same act on the board below is the same mark. Only on your own turn,
                      and never from a sandbox.
                    */}
                    {canAct && !readOnly && !gone && (
                      <button type="button" className="bapick"
                        title={`Open the bidding on ${t.player}`}
                        aria-label={`Nominate ${t.player}`}
                        onClick={() => void nominate(t.player, prospectOf(t.player))} />
                    )}
                    <span className="baname"><b>{last}</b> <i>{first}</i></span>
                    {t.position && <span className={`poschip ident pos-${t.position}`}>{t.position}</span>}
                    {/*
                      HE IS GONE WHEN THE ROOM HAS HIM, and the row says who by rather than
                      vanishing: a name disappearing off a list you wrote is the one thing that
                      makes a man doubt the list. Struck through, no controls, and the × still
                      takes him off when he is done with him.
                    */}
                    {gone ? (
                      <span className="fqgone">nominated by {managerShort(gone)}</span>
                    ) : (!readOnly && (<>
                      {/*
                        THREE CIRCLES, ONE FAMILY — the picklist's controls, not lookalikes of
                        them (Tim, 8 September). Every mark is DRAWN from the button's own
                        pseudo-elements: two bars turned 45° for the ×, a square with two borders
                        turned for the caret. The arrow glyphs these replace were a different
                        weight and a different centre in every face the app might fall back to,
                        and they sat a pixel high beside a plus that is drawn.
                      */}
                      <span className="pqmove">
                        <button type="button" className="pqarrow up" disabled={i === 0}
                          title={`Move ${last} up`} aria-label={`Move ${last} up`}
                          onClick={() => moveTarget(i, i - 1)} />
                        <button type="button" className="pqarrow down" disabled={i === targets.length - 1}
                          title={`Move ${last} down`} aria-label={`Move ${last} down`}
                          onClick={() => moveTarget(i, i + 1)} />
                      </span>
                      <button type="button" className="pqx"
                        title={`Take ${last} off your targets`}
                        aria-label={`Take ${last} off your targets`}
                        onClick={() => dropTarget(t.player)} />
                    </>))}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
        )}

        {fqTab === 'board' && (<>

        {/* -- what I am looking for, and what there is ------------------------------------ */}
        {/*
          THE BOARD ITSELF, SEARCHABLE AND FILTERABLE (Tim, 6 September: "list the college and
          high school players in a searchable and filterable list"). The nomination panel's list
          is driven by the auction card's field and exists to open a lot; this is the 2027 board
          to read at leisure — every eligible name, college and high school together, the way
          `Best available` sits under the main draft's picklist. A high schooler is marked rather
          than separated: he is eligible on the same terms, and the class is the interesting part.
        */}
        <div className="basearch">
          <input className="search" type="search" value={fquery}
            placeholder="Name, initials or school…"
            aria-label="Search the prospect board"
            onChange={(e) => setFquery(e.target.value)} />
        </div>
        <div className="bafilter" role="group" aria-label="Filter by position">
          <button type="button" aria-pressed={fpos.length === 0}
            className={`chip${fpos.length === 0 ? ' on' : ''}`}
            onClick={() => setFpos([])}>All</button>
          {FANTASY_POSITIONS.map((x) => (
            <button type="button" key={x} aria-pressed={fpos.includes(x)}
              className={`chip${fpos.includes(x) ? ' on' : ''}`}
              onClick={() => setFpos((prev) => (prev.includes(x)
                ? prev.filter((y) => y !== x)
                : [...prev, x]))}>{x}</button>
          ))}
          {/* HS is not a position, so it does not clear the others — it narrows to the class. */}
          <button type="button" aria-pressed={hsOnly}
            className={`chip${hsOnly ? ' on' : ''}`}
            title="Only the class of 2027 high schoolers"
            onClick={() => setHsOnly((v) => !v)}>HS</button>
        </div>
        <ul className="balist fqboard">
          {pool.map((p) => {
            const on = watched.has(norm(p.n));
            const { last, first } = splitName(p.n);
            return (
              <li key={p.n} title={`${p.n} · ${p.p} · ${p.s}${p.hs ? ' (high school)' : ''}`}>
                {/*
                  THE DEVY RANK IS THE NUMBER NOW (Tim, 9 September: "use the devy 300 as my top
                  source then"). It falls back to the draft-board rank for the men devy does not
                  carry, and to nothing at all for the men neither does — a blank is the honest
                  answer for a name no board has ranked, and the row is still nominable.
                */}
                {p.devy ?? p.overall
                  ? <span className="pqn">{p.devy ?? p.overall}</span>
                  : <span className="pqn" />}
                {/*
                  AND THE SAME PLUS ON THE BOARD, where the main draft's player list has it: the
                  act first, then who he is. A man reading the class of 2027 when his turn opens
                  should be able to stand somebody up from the row he is already reading rather
                  than typing the name he is looking at into the auction card's field.
                */}
                {canAct && !readOnly && (
                  <button type="button" className="bapick"
                    title={`Open the bidding on ${p.n}`}
                    aria-label={`Nominate ${p.n}`}
                    onClick={() => void nominate(p.n, p)} />
                )}
                <span className="baname"><b>{last}</b> <i>{first}</i></span>
                <span className={`poschip ident pos-${p.p}`}>{p.p}</span>
                {p.hs && <span className="hsmark" aria-label="high school">HS</span>}
                <span className="fqschool">{p.s}</span>
                {/*
                  Adding only while the board is read-only, exactly as on the main draft: a man
                  looking at a sandbox or an undrawn order may still find somebody he wants, but
                  taking a name OFF a real list from a screen that can write nothing else is the
                  edit to leave alone.
                */}
                <button type="button" className={`fqstar${on ? ' on' : ''}`}
                  disabled={on && readOnly}
                  title={on
                    ? readOnly
                      ? `${p.n} is on your targets — take him off from the live auction`
                      : `${p.n} is on your targets — click to take him off`
                    : `Put ${p.n} on your targets`}
                  aria-label={on ? `Take ${p.n} off your targets` : `Watch ${p.n}`}
                  onClick={() => (on ? dropTarget(p.n) : addTarget(p))}>
                  {on ? '\u2605' : '\u2606'}
                </button>
              </li>
            );
          })}
          {pool.length === 0 && (
            <li className="empty">
              <span className="mut">Nobody on the board matches that.</span>
            </li>
          )}
        </ul>
        <p className="railfoot">
          {pool.length === poolAll.length
            ? `${pool.length} on the board`
            : `${pool.length} of ${poolAll.length}`} · QB/RB/WR/TE, boards read {bank.readOn}
        </p>
        </>)}
      </details>

      {/*
        WHO HOLDS WHAT, IN THE RAIL (Tim, 9 September: "move 'farm rosters' to rail under prospect
        lists"). It is a standing reference read down a column — ten names, three slots each — and
        under the board it was sharing a `1fr 1fr` row with a filtered table, which made each the
        other's width. In the rail it reads as a list, and Past auctions gets the whole column.
      */}
      <FarmRosters order={order} tonight={log} />

     </div>
     )}

     {railOpen && onRailW && <RailGrip w={railW} onW={onRailW} />}

     {/*
       The main column, in the drawing's order: the grid, the nomination search, then the two
       reference panels SIDE BY SIDE beneath them — 13a draws those as `1fr 1fr` inside this
       column, not as a full-width strip under everything.

       `.draftcols` is a two-track grid, so all of this has to share one child; a third child
       wraps into the 320px rail track, which is what squeezed the old nominations table into a
       strip and is worth not rediscovering.
     */}
     <div className="farmmain">
      {/* 13a: the grid, not a table — the draft board's object at the auction's scale. */}
      <section className="card">
        <div className="striptop">
          <h3 className="striphead">Nominations</h3>
          <span className="mut mono">
            {sold} of {turns.length} · round {current?.round ?? rounds} of {rounds}
          </span>
        </div>
        <NomGrid turns={turns} order={order} me={actor} />

        {/*
          Undoing a sale, from the grid rather than from the card — by the time somebody says "he
          wasn't finished bidding" the card has moved on to the next nomination, and the sale you
          want back is three lots ago.
        */}
        {privileged && sold > 0 && (
          <div className="lotfix">
            <span className="mut">Put a sale back</span>
            {turns.filter((t) => t.rec?.winner).slice(-4).reverse().map((t) => (
              <button type="button" className="btn small ghost" key={`${t.round}.${t.slot}`}
                title={`Reopen the bidding on ${t.rec!.player} — he goes back up at ${faab(t.rec!.bid ?? 0)}`}
                onClick={() => void unsell(t)}>
                {t.rec!.player} · {managerShort(t.rec!.winner!)}
              </button>
            ))}
          </div>
        )}
        {/*
          THE WHOLE ROUND, BACK TO NOTHING.
          Undoing lots one at a time is right for a mistake in the room. It is the wrong tool for
          the state Tim was actually in — a farm round part-entered from testing, nothing about it
          worth keeping, and twenty turns to walk back by hand. This wipes the season's
          nominations and leaves the turn order exactly as it was.

          Typed to confirm, because it is the only control on this screen that cannot be undone by
          another control on this screen. `reset` is four letters and enough of a pause.
        */}
        {privileged && turns.some((t) => t.rec) && (
          <div className="lotfix danger">
            <span className="mut">Start the farm round again</span>
            {resetting ? (
              <>
                <input className="search tiny" value={confirm} autoFocus
                  aria-label="Type reset to confirm"
                  placeholder="type reset"
                  onChange={(e) => setConfirm(e.target.value)} />
                <button type="button" className="btn small primary"
                  disabled={confirm.trim().toLowerCase() !== 'reset' || wiping}
                  onClick={() => void resetRound()}>
                  {wiping ? 'Clearing…' : `Clear all ${turns.filter((t) => t.rec).length}`}
                </button>
                <button type="button" className="linkbtn tiny"
                  onClick={() => { setResetting(false); setConfirm(''); }}>cancel</button>
              </>
            ) : (
              <button type="button" className="btn small ghost"
                title="Clears every nomination, bid and sale for this season’s farm round. The
                  turn order and the prospect pool are untouched."
                onClick={() => setResetting(true)}>Reset the farm round</button>
            )}
          </div>
        )}
      </section>

      {/*
        THE ROOM'S CONTROLS, THE BOARD'S FOUR, IN THE BOARD'S ORDER (Tim, 8 September: "need
        start/pause, reset pick, reset draft, undo/redo controls on the farm rounds just like the
        main draft").

        Same marks, same grouping, same words — `.ccbar` and `.ccbtn` are the board's own classes
        and they are reused rather than copied, because two control bars that are meant to look
        alike will drift the moment they have two stylesheets. What differs is what the words point
        at: a lot rather than a pick, and a sale rather than a cell, because those are this
        screen's units.
      */}
      {privileged && !locked && (
        <div className="ccbar farmcc">
          <span className={`dstatus ccstate ${paused ? 'paused' : 'live'}`}>
            {draft?.farmStatus === 'complete' ? 'Complete'
              : farmLive ? 'Live now'
                : draft?.farmStartedAt ? 'Paused' : 'Not started'}
          </span>

          <span className="ccgroup ccedits">
            <button type="button" className="ccbtn" disabled={!lastSale}
              title={lastSale
                ? `Put ${lastSale.rec?.player} back up at ${faab(lastSale.rec?.bid ?? 0)} — `
                  + `${managerShort(lastSale.rec?.winner ?? '')} stops owning him and the lot `
                  + 'reopens for the room'
                : 'No sale to undo yet'}
              onClick={() => void undoLastSale()}>
              <UndoMark /> Undo sale
              {lastSale && (
                <span className="ccundo">
                  {' · '}{nomCode(lastSale.round, lastSale.slot)}{' '}
                  {shortSurname(splitName(lastSale.rec?.player ?? '').last)}
                </span>
              )}
            </button>
            {redo.length > 0 && (
              <button type="button" className="ccbtn"
                title={`Sell ${redo[0]!.player} to ${managerShort(redo[0]!.winner)} again for `
                  + `${faab(redo[0]!.bid)} · ${redo.length} to redo`}
                onClick={() => void redoLastSale()}>
                <UndoMark forward /> Redo
              </button>
            )}
          </span>

          <span className="ccgroup ccclocks">
            <button type="button"
              className={`ccplay ${farmLive ? 'running' : 'stopped'}`}
              aria-pressed={farmLive}
              title={farmLive
                ? 'Stop the going-gone clock without ending the auction'
                : draft?.farmStatus === 'complete' ? 'Reopen the farm rounds'
                  : 'Start the auction — the going-gone clock begins selling lots by itself'}
              onClick={() => void setFarmStatus(farmLive ? 'setup' : 'live')}>
              <span className="ccplayic" aria-hidden="true">
                {farmLive ? <PauseMark /> : <PlayMark />}
              </span>
              <span className="ccplaylab">
                {farmLive ? 'Pause' : draft?.farmStatus === 'complete' ? 'Reopen' : 'Start'}
              </span>
            </button>
            <button type="button" className="ccbtn" disabled={!live && !current}
              title={live
                ? 'Give this lot its going-gone clock back, without stopping the auction'
                : 'Give the man nominating his full time back'}
              onClick={() => void restartLot()}>
              <RestartIcon /> Lot
            </button>
            <button type="button" className="ccbtn"
              title="Clear every nomination and sale of the farm rounds and start them over"
              onClick={() => setResetting(true)}>
              <RestartIcon /> Rounds
            </button>
          </span>
        </div>
      )}

      {/*
        THE COMMISSIONER'S BIDDING TABLE, under the board and above the nomination panel — the
        two things one person is doing all evening, in the order he does them: take the bids, then
        take the next nomination. Not in the rail, which is 300px wide and already holds the
        reading version of this list.

        EVERYBODY SEES IT, and `canBidFor` decides per row who may press anything — a commissioner
        types for all ten, a GM only for himself. It was commissioner-only while the rail carried
        `Who is bidding` for everybody else; with that gone, gating this panel would leave a GM
        unable to see the room at all. His own bid is still the auction card, which is bigger,
        closer and his.
      */}
      {!locked && (
        <section className="card bidtablesec">
          <div className="roomhead">
            <h3 className="striphead">Take a bid</h3>
            <span className="mut">
              {live?.rec?.player
                ? <>on <b>{live.rec.player}</b></>
                : 'nothing is up for bid yet'}
            </span>
          </div>
          <BidTable rows={bidRows} nextBid={view.nextBid} high={view.high}
            live={Boolean(live?.rec?.player) && !live?.rec?.winner}
            canBidFor={canBidFor}
            onRaise={(to, gm) => void raise(to, gm)}
            onPass={(gm) => void passBid(gm)}
            onUnpass={(gm) => void unpass(gm)} />
          {refused && <p className="note warn btrefused">{refused}</p>}

          {/* The cap that empties a row, said once under the list rather than on every row. */}
          <p className="railfoot">
            A GM stops bidding once his farm or his yearly allowance is full. 2017-II-5 §6
            {live && <> · {alive.length} still live on {live.rec?.player}</>}
          </p>

          {/*
            THE HAMMER, AT THE FOOT OF THE LIST HE IS READING. It was in the rail beside the other
            copy of these ten rows; the man about to bring it down is looking at this table.
          */}
          {privileged && live?.rec?.highBidder && !live.rec.winner && (
            <div className="acacts">
              <button type="button" className="btn primary wide" onClick={() => void hammer()}>
                Sold to {managerShort(live.rec.highBidder)} for {faab(live.rec.highBid ?? 0)}
              </button>
            </div>
          )}

          {/* Every one of these is reversible, and every one is a commissioner's. */}
          {privileged && live && (
            <div className="lotfix">
              <span className="mut">Commissioner</span>
              <button type="button" className="btn small ghost"
                disabled={!live.rec?.highBidder}
                title="Wipe the room's bidding and the passes; the player stays up at the nominator's opening ₣1"
                onClick={() => void resetBids()}>Clear bids</button>
              <button type="button" className="btn small ghost"
                title={`Take ${live.rec?.player ?? 'him'} back off and hand the turn to ${managerShort(live.nominator)} again`}
                onClick={() => void takeBack()}>Wrong player</button>
            </div>
          )}
        </section>
      )}

      {/*
        WHAT IS LEFT OF THE NOMINATION PANEL (Tim, 9 September: "remove the panel labeled 'round 1,
        nomination 3 - ryan' and combine any lost function into another panel (not sure there is
        any lost functionality)").

        He was right about the list. It was a third copy of the same names — the auction card has
        the field, the rail's board has every prospect with a `+` on each row that opens the
        bidding, and this panel repeated the field's matches with a `Choose` button. The pass is on
        the card too. So the list, the Choose buttons and the pass came out.

        These two lines did not, because nothing else says them: the board being open is the reason
        the whole screen is inert, and a finished auction is a thing to be told rather than to
        deduce from a board with no next name on it.
      */}
      {locked ? (
        <section className="card farmnote">
          <p className="note">
            The board is still open. Nominations begin once the draft is marked complete.
          </p>
        </section>
      ) : !current ? (
        /*
          THE LAST ACT OF THE EVENING (Tim, 9 September: "all the picks have been made in farm. need
          a main draft-live mark as completed option to lock everything in and write to history/past
          auctions"). The main draft has had this since it was written and the farm rounds ended by
          simply running out of turns, which is not the same thing: a board with twenty rows filled
          in is a board that could still be reopened by a mis-click, and nothing on the screen said
          the evening was over.

          Marking it complete is a state, not a copy: `farmStatus: 'complete'` stops the clocks, and
          the panels already read tonight's sales beside every previous year — see `salesTonight`.
          The button is REOPEN afterwards rather than gone, because "we are not done after all" is a
          thing that happens in a room at eleven at night.
        */
        <section className="card farmnote done">
          <h3 className="striphead">
            {draft?.farmStatus === 'complete' ? 'Farm rounds closed' : 'Every nomination is in'}
          </h3>
          <p className="note">
            {draft?.farmStatus === 'complete'
              ? `All ${turns.length} nominations are in and the ${SEASON} auction is closed. `
                + 'Tonight\u2019s sales are in the farm log and on every roster.'
              : `All ${turns.length} nominations are in. Marking the rounds complete stops the `
                + 'clocks and files the evening.'}
          </p>
          {privileged && (
            <div className="farmnoteacts">
              <button type="button"
                className={`btn ${draft?.farmStatus === 'complete' ? '' : 'primary'}`}
                onClick={() => void setFarmStatus(
                  draft?.farmStatus === 'complete' ? 'live' : 'complete',
                )}>
                {draft?.farmStatus === 'complete'
                  ? 'Reopen the farm rounds'
                  : 'Mark the farm rounds complete'}
              </button>
              {draft?.farmStatus !== 'complete' && (
                <span className="mut">
                  {nomSoldCount(turns).sold} sold of {turns.length}
                </span>
              )}
            </div>
          )}
        </section>
      ) : null}

      <FarmPast order={order} tonight={log} />
     </div>
    </div>
    </>
  );
}
